import React, { Component } from "react";
import ClearFilterButton from "../../components/ClearFilterButton.js";
import {
  loading,
  alert,
  NavbarAdmin,
  Pagination,
  Select,
  DateRangeFields,
  GET,
  GET_FILE,
  saveAs,
  t,
} from "../../components/CustomComponent.js";
import {
  fill,
  fmtInt,
  fmtBaht,
  fmtDate,
  fmtDay,
  StatusBadge,
  WarrantyBadge,
  OpenBadge,
  EmptyBox,
  Skeleton,
  ErrorCard,
} from "../../components/AssetHistoryParts.js";
import { pruneParams } from "../../utils/timeTracking.js";
import "../../assets/css/asset-history.css";
import { formatNumber } from "../../utils/formatting";

const PAGE_SIZE = 10;
const Q_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const STATUSES = ["active", "inactive"];
const WARRANTIES = ["in", "near", "expired", "none"];
const REPAIRS = ["open", "repeat"];
const NO_DEPARTMENT_PATTERN = "ไม่มีสิทธิ์ดู";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ACCESS_DENIED = "Access denied";

const pick = (value, allowed) => (allowed.includes(value) ? value : "");

// หน้า ② ค้นหา / เรียกดูสินทรัพย์ - GET admin/asset-history-report (สิทธิ์ asset.read)
// รับตัวกรองจาก query string (?q=&department_uid=&status=&warranty=&repair=) · พิมพ์ค้นหา >= 2 ตัวอักษร (debounce 300ms)
// Serial / Asset ID ตรงทั้งตัวและเจอเครื่องเดียว (meta.exact_match_uid) -> เปิดหน้า ③ ทันที
export default class AdminAssetHistoryList extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(window.location.search);
    const departmentUid = (query.get("department_uid") || "").trim();
    this.state = {
      q: query.get("q") || "",
      department_uid: UUID_RE.test(departmentUid) ? departmentUid : "",
      status: pick(query.get("status"), STATUSES),
      warranty: pick(query.get("warranty"), WARRANTIES),
      repair: pick(query.get("repair"), REPAIRS),
      from_date: "",
      to_date: "",
      sort_by: "",
      sort_dir: "desc",
      page: 1,
      rows: [],
      count: 0,
      loaded: false,
      fetching: true,
      error: "",
      forbidden: "",
      department_options: [],
      exporting: false,
    };
    this.q_timer = null;
    this.req_id = 0;
    this.last_search = ""; // คำค้นที่ใช้จริงล่าสุด (>= 2 ตัว) กันยิงซ้ำเมื่อพิมพ์ไม่เปลี่ยนผล
  }

  componentDidMount() {
    loading(false);
    this.last_search = this.effectiveQ(this.state.q);
    this.Get(1);
  }

  componentWillUnmount() {
    clearTimeout(this.q_timer);
  }

  effectiveQ = (q) => (q.trim().length >= MIN_SEARCH_LENGTH ? q.trim() : "");

  buildParams = () => {
    const { q, department_uid, status, warranty, repair, from_date, to_date, sort_by, sort_dir } = this.state;
    return pruneParams({ q: this.effectiveQ(q), department_uid, status, warranty, repair, from_date, to_date, sort_by, sort_dir: sort_by ? sort_dir : "" });
  };

  // เก็บตัวกรองไว้ใน URL เพื่อให้ย้อนกลับจากหน้า ③ แล้วตัวกรองยังอยู่
  SyncUrl = () => {
    const { q, department_uid, status, warranty, repair } = this.state;
    const qs = new URLSearchParams(pruneParams({ q: this.effectiveQ(q), department_uid, status, warranty, repair })).toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
  };

  Get = async (page = 1, typed = false) => {
    const { from_date, to_date } = this.state;
    if (from_date && to_date && from_date > to_date) {
      alert("", "warning", t("warning"), t("nsp_range_invalid"));
      this.setState({ rows: [], count: 0, page: 1, loaded: true, fetching: false });
      return;
    }
    const req = ++this.req_id;
    this.setState({ fetching: true, error: "", forbidden: "" });
    const result = await GET("admin/asset-history-report", { ...this.buildParams(), page, limit: PAGE_SIZE }, true, { skipAccessDeniedAlert: true });
    if (req !== this.req_id) return;
    if (result?.status && result.data) {
      const rows = result.data.rows || [];
      const exact = result.data.meta?.exact_match_uid;
      // ตัวเลือกแผนกตามสิทธิ์ผู้ใช้มากับ meta ของรายการ
      if (Array.isArray(result.data.meta?.department_options)) this.setState({ department_options: result.data.meta.department_options });
      this.SyncUrl();
      if (typed && exact) {
        this.GoDetail(exact);
        return;
      }
      this.setState({ rows, count: result.pagination?.count ?? rows.length, page, loaded: true, fetching: false });
      return;
    }
    const message = typeof result?.message === "string" ? result.message : "";
    const forbidden = message === ACCESS_DENIED || message.includes(NO_DEPARTMENT_PATTERN) ? message : "";
    this.setState({ rows: [], count: 0, page: 1, loaded: true, fetching: false, forbidden, error: forbidden ? "" : message || t("ah_error_hint") });
  };

  SetFilter = (patch) => this.setState(patch, () => this.Get(1));

  SetQ = (value) => {
    this.setState({ q: value });
    clearTimeout(this.q_timer);
    this.q_timer = setTimeout(() => {
      const next = this.effectiveQ(this.state.q);
      if (next === this.last_search) return; // ยังสั้นกว่า 2 ตัว/ผลค้นหาเท่าเดิม ไม่ต้องยิง
      this.last_search = next;
      this.Get(1, !!next);
    }, Q_DEBOUNCE_MS);
  };

  ResetFilter = () => {
    clearTimeout(this.q_timer);
    this.last_search = "";
    this.SetFilter({ q: "", department_uid: "", status: "", warranty: "", repair: "", from_date: "", to_date: "", sort_by: "", sort_dir: "desc" });
  };

  SortBy = (field) => {
    const { sort_by, sort_dir } = this.state;
    this.SetFilter({ sort_by: field, sort_dir: sort_by === field && sort_dir === "asc" ? "desc" : "asc" });
  };

  GoDetail = (uid) => {
    window.location.href = `/ad/asset-history-detail?uid=${encodeURIComponent(uid)}`;
  };

  Export = async () => {
    this.setState({ exporting: true });
    loading(true);
    const result = await GET_FILE("admin/asset-history-report/export", this.buildParams());
    loading(false);
    this.setState({ exporting: false });
    if (result?.status && result.blob) {
      saveAs(result.blob, result.fileName || "Asset_History.xlsx");
    } else {
      // เกิน 10,000 แถว = backend ตอบ 400 พร้อมข้อความไทย แสดงตรง ๆ
      alert("", "warning", t("warning"), result?.message || t("ah_export_failed"));
    }
  };

  // ---------------------------------------------------------------- render
  renderSortTh = (field, label, className = "") => {
    const { sort_by, sort_dir } = this.state;
    return (
      <th className={`border-0 ah-th-sort ${className}`} onClick={() => this.SortBy(field)}>
        {label} {sort_by === field ? (sort_dir === "asc" ? "▲" : "▼") : ""}
      </th>
    );
  };

  renderTable() {
    const { rows } = this.state;
    const th = (label, cls = "") => <th className={`border-0 ${cls}`}>{label}</th>;
    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0 ah-table" data-testid="ah-list-table">
          <thead>
            <tr className="text-dark fw-bold border-bottom border-light">
              {this.renderSortTh("asset_code", t("ah_col_asset_id"), "nw")}
              {th(t("ah_col_product"))}
              {th(t("ah_col_serial"), "nw")}
              {th(t("ah_col_customer"))}
              {th(t("ah_col_bu"), "nw")}
              {th(t("ah_col_project"))}
              {th(t("ah_col_status"), "nw")}
              {this.renderSortTh("warranty_expire", t("ah_col_warranty_latest"), "nw")}
              {this.renderSortTh("last_repair_at", t("ah_col_last_repair"), "nw")}
              {this.renderSortTh("repair_count", t("ah_col_repairs"), "nw num")}
              {this.renderSortTh("total_cost", t("ah_col_total_cost_all"), "nw num")}
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr>
                <td colSpan={11}>
                  <EmptyBox icon="🔍" title={t("ah_list_empty")} hint={t("ah_list_empty_hint")}>
                    <ClearFilterButton onClick={this.ResetFilter} />
                  </EmptyBox>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.uid} className="border-bottom border-light bg-white ah-row" onClick={() => this.GoDetail(r.uid)} data-testid="ah-list-row">
                  <td className="ref">{r.asset_code}</td>
                  <td className="ah-prod wrap"><b>{r.product_name || "-"}</b><span>{[r.brand_name, r.part_number].filter(Boolean).join(" · ")}</span></td>
                  <td className="nw">{r.serial_number || "-"}</td>
                  <td className="wrap">{r.customer_name || "-"}<div className="sub">{[r.location_name, r.location_province].filter(Boolean).join(" · ")}</div></td>
                  <td className="wrap" title={r.department ? [r.department.code, r.department.name].filter(Boolean).join(" ") : ""}>{r.department ? <><span className="ah-bd bu">{r.department.code || "-"}</span><div className="sub">{r.department.name}</div></> : <span className="text-secondary">-</span>}</td>
                  <td className="wrap sub">{r.project_name || "-"}</td>
                  <td className="nw"><StatusBadge status={r.status} /></td>
                  <td className="nw"><WarrantyBadge warranty={r.warranty} /><div className="sub">{r.warranty?.expire_date ? fill(t("ah_expire_on"), { d: fmtDate(r.warranty.expire_date) }) : ""}</div></td>
                  <td className="nw">
                    {r.last_repair_at ? fmtDay(r.last_repair_at) : <span className="sub">{t("ah_never_repaired")}</span>}
                    {r.has_open_work_order ? <div><OpenBadge /></div> : null}
                  </td>
                  <td className="num nw fw-bold">{fmtInt(r.repair_count)}</td>
                  <td className="num nw">{fmtBaht(r.total_cost)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  renderResults() {
    const { rows, count, page, loaded, fetching, error, forbidden } = this.state;
    if (forbidden) {
      return (
        <div className="bg-white rounded-4 shadow-sm p-5 mb-4 text-center" data-testid="ah-forbidden">
          <h4 className="fw-bold text-dark">{forbidden === ACCESS_DENIED ? t("ah_forbidden") : forbidden}</h4>
          <p className="text-secondary text-14 mb-3">{t("ah_forbidden_hint")}</p>
          <button type="button" className="btn btn-primary px-4 py-2 fw-bold rounded-3" onClick={() => this.SetFilter({ department_uid: "" })}>{t("ah_all_bu")}</button>
        </div>
      );
    }
    if (error) return <ErrorCard message={error} onRetry={() => this.Get(page)} />;
    if (!loaded) return <Skeleton heights={[60, 320]} />;
    const from = count ? (page - 1) * PAGE_SIZE + 1 : 0;
    const to = count ? from + rows.length - 1 : 0;
    return (
      <>
        <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden" style={fetching ? { opacity: 0.55 } : undefined}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-4 pt-3 pb-2 text-12 text-secondary">
            <span data-testid="ah-result-count">{count ? fill(t("ah_found"), { n: fmtInt(count), from: formatNumber(from), to: formatNumber(to) }) : ""}</span>
            <span>{t("ah_list_hint")}</span>
          </div>
          {this.renderTable()}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap text-secondary text-12">
          <div>{t("showing")} {formatNumber(from)}-{formatNumber(to)} {t("of")} {formatNumber(count)}</div>
          <Pagination className="mb-0" count={Math.ceil(count / PAGE_SIZE) || 1} page={page} showFirstButton={false} showLastButton={false} onChange={(_, next) => this.Get(next)} />
        </div>
      </>
    );
  }

  render() {
    const { q, department_uid, status, warranty, repair, from_date, to_date, department_options, exporting, loaded, count, error, forbidden } = this.state;
    const departmentOptions = [{ value: "", label: t("ah_all_bu") }, ...department_options.map((d) => ({ value: d.uid, label: [d.code, d.name].filter(Boolean).join(" ") }))];
    // แผนกจากลิงก์ที่ไม่อยู่ในรายการ active - ใช้ชื่อจากแถวรายการ (ถ้ามี)
    if (department_uid && !departmentOptions.some((o) => o.value === department_uid)) {
      const d = this.state.rows.find((r) => r.department?.uid === department_uid)?.department;
      departmentOptions.push({ value: department_uid, label: d ? [d.code, d.name].filter(Boolean).join(" ") : t("ah_f_bu") });
    }
    const short = q.trim().length > 0 && q.trim().length < MIN_SEARCH_LENGTH;
    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <a href="/ad/asset-report" className="text-14 text-decoration-none" style={{ color: "#2A55E5" }}>‹ {t("ah_back_overview")}</a>
              <h3 className="fw-bold mb-1 mt-1 text-dark">{t("ah_list_title")}</h3>
              <div className="text-secondary text-14">{t("ah_list_desc")}</div>
            </div>
            <button type="button" className="btn ah-btn-excel px-4 py-2 fw-bold rounded-3" onClick={this.Export} disabled={exporting || !loaded || !!error || !!forbidden || !count} data-testid="ah-export-btn">
              ⬇ Excel
            </button>
          </div>

          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-6 col-xl-4">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_search")}</label>
                <input type="text" className="form-control" value={q} placeholder={t("ah_f_search_ph")} onChange={(e) => this.SetQ(e.target.value)} data-testid="ah-f-search" />
                {short ? <div className="text-12 text-secondary mt-1">{fill(t("ah_search_min"), { n: MIN_SEARCH_LENGTH })}</div> : null}
              </div>
              <div className="col-md-3 col-xl-2" data-testid="ah-f-bu">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_bu")}</label>
                <Select className="form-control w-100" options={departmentOptions} value={department_uid} onChange={(v) => this.SetFilter({ department_uid: v || "" })} placeholder={t("ah_all_bu")} />
              </div>
              <div className="col-md-3 col-xl-2" data-testid="ah-f-status">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_status")}</label>
                <Select
                  className="form-control w-100"
                  options={[{ value: "", label: t("ah_all_status") }, { value: "active", label: t("ah_s_active") }, { value: "inactive", label: t("ah_s_inactive") }]}
                  value={status}
                  onChange={(v) => this.SetFilter({ status: v || "" })}
                  placeholder={t("ah_all_status")}
                />
              </div>
              <div className="col-md-3 col-xl-2" data-testid="ah-f-warranty">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_warranty")}</label>
                <Select
                  className="form-control w-100"
                  options={[{ value: "", label: t("ah_all_warranty") }, { value: "in", label: t("ah_w_in") }, { value: "near", label: t("ah_f_w_near") }, { value: "expired", label: t("ah_w_expired") }, { value: "none", label: t("ah_f_w_none") }]}
                  value={warranty}
                  onChange={(v) => this.SetFilter({ warranty: v || "" })}
                  placeholder={t("ah_all_warranty")}
                />
              </div>
              <div className="col-md-3 col-xl-2" data-testid="ah-f-repair">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_repair")}</label>
                <Select
                  className="form-control w-100"
                  options={[{ value: "", label: t("ah_all_repair") }, { value: "open", label: t("ah_f_r_open") }, { value: "repeat", label: t("ah_f_r_repeat") }]}
                  value={repair}
                  onChange={(v) => this.SetFilter({ repair: v || "" })}
                  placeholder={t("ah_all_repair")}
                />
              </div>
              <div className="col-md-6 col-xl-4" data-testid="ah-f-date">
                <label className="form-label text-12 text-secondary mb-1">{t("ah_f_last_repair")}</label>
                <div className="d-flex gap-2">
                  <DateRangeFields
                    type="th"
                    className="form-control"
                    fieldStyle={{ flex: 1, minWidth: 0 }}
                    startPlaceholder={t("start_date")}
                    endPlaceholder={t("end_date")}
                    startValue={from_date}
                    endValue={to_date}
                    onChange={({ start, end }) => this.SetFilter({ from_date: start, to_date: end })}
                  />
                </div>
              </div>
              <div className="col-auto">
                <ClearFilterButton onClick={this.ResetFilter} />
              </div>
            </div>
          </div>

          {this.renderResults()}
        </div>
      </div>
    );
  }
}
